/**
 * Hook to redirect a user to the Booking Engine (Extranet) via SSO.
 *
 * API Endpoint: GET /v4/sso/to-booking-engine
 * 
 * Obtains a signed token and redirects the user to the Extranet
 * with the encrypted data and signature as query params.
 */

import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { AxiosError } from "axios";
import {
    RedirectToBookingEngineResponseSchema,
    type RedirectToBookingEngineResponse,
} from "../response/RedirectToBookingEngineResponse";

const rawBookingEngineUrl = import.meta.env.VITE_BOOKING_ENGINE_URL || "https://extranet.bedshero.com";
const BOOKING_ENGINE_URL = rawBookingEngineUrl.startsWith("http") ? rawBookingEngineUrl : `http://${rawBookingEngineUrl}`;

export const useRedirectToBookingEngine = () => {
    return useMutation<RedirectToBookingEngineResponse, AxiosError>({
        mutationFn: async () => {
            const response = await privateApi.get<RedirectToBookingEngineResponse>(
                "/v4/sso/to-booking-engine"
            );

            console.log('response api', response)

            // Validate response with Zod
            const validationResult = RedirectToBookingEngineResponseSchema.safeParse(response.data);
            if (!validationResult.success) {
                console.error("SSO redirect validation failed:", validationResult.error.errors);
                throw new Error("Invalid SSO redirect response format");
            }

            return validationResult.data;
        },
        onSuccess: (data) => {
            if (!data.success) {
                console.error("SSO redirect was not successful:", data);
                throw new Error("SSO redirect request was not successful");
            }

            // Construct the Booking Engine URL with signed data
            const redirectUrl = new URL("/sso/login", BOOKING_ENGINE_URL);
            redirectUrl.searchParams.set("data", data.data);
            redirectUrl.searchParams.set("signature", data.signature);

            // Redirect the user to the Booking Engine
            window.open(redirectUrl.toString(), '_blank');
        },
        onError: (error) => {
            console.error("Failed to initiate SSO redirect to Booking Engine:", error);

            if (error.code === "ECONNABORTED") {
                console.error("SSO redirect request timed out");
            }
        },
        retry: false, // SSO redirects should not auto-retry
    });
};
